import { useMemo, useRef, useState } from 'react';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  InboxOutlined,
  ProfileOutlined,
  RedoOutlined,
  ReloadOutlined,
  ScanOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Button, Empty, Input, Select } from '../../../components/ui';
import { cn } from '../../../lib/cn';
import { useLoi } from '../../../hooks/useLoi';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { usePermission, useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { NHAN_LOAI } from '../../cau-hinh-ocr/constants';
import { DOCUMENT_TYPES, type DocumentType } from '../../cau-hinh-ocr/types';
import { OcrStageBadge, TypeBadge } from '../components/badges';
import { UserCell } from '../components/UserCell';
import { QUET_LAI_DUOC, SUA_DUOC, XOA_DUOC } from '../constants';
import { useDanhSachChungTu, useThongKeChungTu } from '../hooks/useChungTu';
import { useTenNguoi } from '../hooks/useTenNguoi';
import { useThaoTacChungTu } from '../hooks/useThaoTacChungTu';
import type { DocListItem, DocStatus, OcrStage } from '../types';

/**
 * Lựa chọn của bộ lọc trạng thái, gồm cả các giai đoạn thụt lề. Mang khoá; dấu `—` là thụt lề
 * trình bày, ghép ở chỗ dựng chứ không nhét vào catalog.
 */
const STATUS_OPTIONS: ReadonlyArray<readonly [string, KhoaDich, boolean]> = [
  ['PROCESSING', 'chungTu.locTatCa', false],
  ['PROCESSING:QUEUED', 'chungTu.giaiDoan.QUEUED', true],
  ['PROCESSING:READING', 'chungTu.giaiDoan.READING', true],
  ['PROCESSING:READ_DONE', 'chungTu.giaiDoan.READ_DONE', true],
  ['PROCESSING:MAPPING', 'chungTu.giaiDoan.MAPPING', true],
  ['PROCESSING:RETRY_WAIT', 'chungTu.giaiDoan.RETRY_WAIT', true],
  ['DRAFT', 'chungTu.trangThai.DRAFT', false],
  ['PROCESSED', 'chungTu.trangThai.PROCESSED', false],
  ['CONFIRMED', 'chungTu.trangThai.CONFIRMED', false],
  ['TRANSFERRED', 'chungTu.trangThai.TRANSFERRED', false],
  ['ERROR', 'chungTu.trangThai.ERROR', false],
];

/** Chờ người gõ xong rồi mới hỏi server. */
const CHO_GO = 350;

/** Thẻ số liệu bấm được: bấm vào là đặt bộ lọc trạng thái. */
function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
  active,
  locale,
  onClick,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  icon: React.ReactNode;
  tone: string;
  active: boolean;
  locale: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'col-span-6 flex min-w-0 items-center gap-3 rounded-lg border bg-surface px-4 py-3.5 text-left shadow-sm transition-shadow hover:shadow-md lg:col-span-4 2xl:col-span-2',
        active ? 'border-strong' : 'border-default',
      )}
    >
      <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded', tone)}>{icon}</span>
      <span className="min-w-0">
        <span className="block text-xs leading-snug text-content-secondary">{label}</span>
        <span className="block text-2xl font-bold leading-tight tabular-nums text-content-primary">
          {value === undefined ? '—' : soVN(value, locale)}
        </span>
        {hint && <span className="mt-0.5 block truncate text-2xs text-content-muted">{hint}</span>}
      </span>
    </button>
  );
}

const tenTep = (d: DocListItem, khongTen: string) => d.file_name || khongTen;

/**
 * Quản lý chứng từ (`/xu-ly/chung-tu`, phân hệ `DOC_MGMT`), nối `/api/v1/m/ocr/documents` của module
 * `ocr`. Lọc ở server, phân trang con trỏ bằng `Tải thêm`; còn dòng đang xử lý thì tự hỏi lại.
 */
export function ChungTuView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const navigate = useDieuHuong();
  const tenNguoi = useTenNguoi();
  const thaoTac = useThaoTacChungTu();

  const coSua = useRoutePermission('UPDATE');
  const coXoa = useRoutePermission('DELETE');
  const coChuyen = useRoutePermission('EXPORT');
  const coNhanDang = usePermission('OCR_RECOGNIZE');

  const [oTim, setOTim] = useState('');
  const [tuKhoa, setTuKhoa] = useState('');
  const hen = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [trangThai, setTrangThai] = useState<string | undefined>();
  const [loai, setLoai] = useState<DocumentType | undefined>();
  const [chon, setChon] = useState<string[]>([]);

  const loc = useMemo(() => {
    const [status, stage] = (trangThai ?? '').split(':') as [DocStatus | '', OcrStage | undefined];
    return {
      search: tuKhoa.trim() || undefined,
      status: status ? [status] : undefined,
      ocr_stage: stage ? [stage] : undefined,
      type: loai,
    };
  }, [tuKhoa, trangThai, loai]);

  const q = useDanhSachChungTu(loc);
  const thongKe = useThongKeChungTu();
  const dong = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const coLoc = Boolean(loc.search || trangThai || loai);
  const dangTai = q.isLoading;

  const statusOptions = useMemo(
    () => STATUS_OPTIONS.map(([value, khoa, thutLe]) => ({ value, label: thutLe ? `— ${t(khoa)}` : t(khoa) })),
    [t],
  );
  const typeOptions = useMemo(() => DOCUMENT_TYPES.map((value) => ({ value, label: t(NHAN_LOAI[value]) })), [t]);

  const xoaBoLoc = () => {
    setOTim('');
    setTuKhoa('');
    setTrangThai(undefined);
    setLoai(undefined);
    setChon([]);
  };
  const datTrangThai = (value: string | undefined) => {
    setTrangThai((cu) => (cu === value ? undefined : value));
    setChon([]);
  };
  const moChiTiet = (d: DocListItem) => navigate(`/xu-ly/chung-tu/${d.id}`);
  const ngan = (d: DocListItem) => ({ id: d.id, ten: tenTep(d, t('chungTu.khongTen')) });

  const s = thongKe.data;
  const goiY = s
    ? t('chungTu.goiYGiaiDoan', {
        cho: soVN(s.processing_by_stage.QUEUED, locale),
        doc: soVN(s.processing_by_stage.READING, locale),
        suyLuan: soVN(s.processing_by_stage.READ_DONE + s.processing_by_stage.MAPPING, locale),
      })
    : undefined;

  const columns: TableProps<DocListItem>['columns'] = [
    {
      title: t('chungTu.cotChungTu'),
      width: 320,
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => moChiTiet(row)}
          className="flex min-w-0 max-w-xs items-start gap-2 rounded text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        >
          <FileTextOutlined className="mt-0.5 shrink-0 text-content-muted" />
          <span className="min-w-0">
            <span className="block truncate text-sm text-content-primary" title={tenTep(row, t('chungTu.khongTen'))}>
              {tenTep(row, t('chungTu.khongTen'))}
            </span>
            <span className="block truncate text-xs text-content-muted" title={row.schema.name}>
              {row.schema.name}
              {row.extra_files.length > 0 && ` · ${t('chungTu.phuLuc', { so: row.extra_files.length })}`}
            </span>
          </span>
        </button>
      ),
    },
    {
      title: t('chungTu.cotMa'),
      dataIndex: 'schema_code',
      width: 150,
      render: (code: string) => <span className="font-mono text-xs text-content-secondary">{code}</span>,
    },
    {
      title: t('chungTu.cotLoai'),
      width: 130,
      render: (_v, row) => <TypeBadge type={row.schema.type} />,
    },
    {
      title: t('chungTu.cotSoChungTu'),
      width: 220,
      render: (_v, row) =>
        row.invoice_number || row.seller_name ? (
          <span className="flex min-w-0 max-w-[14rem] flex-col">
            <span className="truncate font-mono text-xs text-content-primary">{row.invoice_number ?? '—'}</span>
            <span className="truncate text-2xs text-content-muted" title={row.seller_name ?? undefined}>
              {row.seller_name ?? '—'}
            </span>
          </span>
        ) : (
          <span className="text-xs text-content-muted">—</span>
        ),
    },
    {
      title: t('chungTu.cotTongTien'),
      width: 140,
      align: 'right',
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
          {row.grand_total === null ? '—' : soVN(Number(row.grand_total), locale)}
        </span>
      ),
    },
    {
      title: t('chungTu.cotNguoiThucHien'),
      width: 180,
      render: (_v, row) => <UserCell name={tenNguoi(row.created_by) ?? '—'} />,
    },
    {
      title: t('chungTu.cotNgayTao'),
      dataIndex: 'created_at',
      width: 110,
      render: (value: string) => <span className="whitespace-nowrap text-xs text-content-muted">{ngayVN(value)}</span>,
    },
    {
      title: t('chung.trangThai'),
      width: 230,
      render: (_v, row) => <OcrStageBadge doc={row} />,
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) => (
        <MenuThaoTac
          tenBanGhi={tenTep(row, t('chungTu.khongTen'))}
          muc={[
            {
              khoa: 'xem',
              nhan: t('chungTu.xemChiTiet'),
              icon: <EyeOutlined />,
              nhom: 'xem',
              onChon: () => moChiTiet(row),
            },
            {
              khoa: 'xac-nhan',
              nhan: t('chungTu.xacNhanChungTu'),
              icon: <CheckOutlined className="text-success-600" />,
              hien: coSua && SUA_DUOC.includes(row.status),
              nhom: 'sua',
              onChon: () => thaoTac.xacNhan(row.id),
            },
            {
              khoa: 'chuyen',
              nhan: t('chungTu.chuyenVaoKho'),
              icon: <SendOutlined />,
              hien: coChuyen && row.status === 'CONFIRMED',
              nhom: 'sua',
              onChon: () => thaoTac.moChuyen(ngan(row)),
            },
            {
              khoa: 'thu-lai',
              nhan: t('chungTu.thuLaiSuyLuan'),
              moTa: t('chungTu.thuLaiSuyLuanMo'),
              icon: <RedoOutlined />,
              hien: coSua && row.status === 'ERROR' && row.ocr_error_step === 'MAP' && row.has_transcript,
              nhom: 'sua',
              onChon: () => thaoTac.thuLaiSuyLuan(row.id),
            },
            {
              khoa: 'quet-lai',
              nhan: t('chungTu.quetLaiTuDau'),
              moTa: t('chungTu.quetLaiMo'),
              icon: <ReloadOutlined />,
              hien: coSua && QUET_LAI_DUOC.includes(row.status),
              nhom: 'sua',
              onChon: () => thaoTac.moQuetLai(ngan(row)),
            },
            {
              khoa: 'huy-quet',
              nhan: t('chungTu.huyLuotQuet'),
              icon: <StopOutlined />,
              hien: coSua && row.status === 'PROCESSING',
              nhom: 'pha-huy',
              onChon: () => thaoTac.moHuyQuet(ngan(row)),
            },
            {
              khoa: 'xoa',
              nhan: t('chungTu.xoaChungTu'),
              icon: <DeleteOutlined />,
              hien: coXoa && XOA_DUOC.includes(row.status),
              nhom: 'pha-huy',
              onChon: () => thaoTac.moXoa(ngan(row)),
            },
          ]}
        />
      ),
    },
  ];

  const coChon = chon.length > 0;

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="grid flex-1 grid-cols-12 content-start gap-4 overflow-y-auto p-4">
        <PageHeader
          className="col-span-12"
          breadcrumb={[{ label: t('nav.nhom.xuLyTaiLieu') }, { label: t('chungTu.tieuDe') }]}
          titleIcon={<ProfileOutlined className="text-primary-600" />}
          title={t('chungTu.tieuDe')}
          subtitle={t('chungTu.moTa')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => {
                  void q.refetch();
                  void thongKe.refetch();
                }}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              {coNhanDang && (
                <Button variant="primary" size="md" icon={<ScanOutlined />} onClick={() => navigate('/xu-ly/nhan-dang')}>
                  {t('chungTu.nhanDangMoi')}
                </Button>
              )}
            </>
          }
        />

        {/* Sáu thẻ số liệu bấm được. Tông icon theo đúng nghĩa trạng thái của thẻ. */}
        <StatCard
          label={t('chungTu.thTongChungTu')}
          value={s?.total}
          locale={locale}
          icon={<ProfileOutlined />}
          tone="bg-subtle text-content-secondary"
          active={!trangThai}
          onClick={() => datTrangThai(undefined)}
        />
        <StatCard
          label={t('chungTu.thDangXuLy')}
          value={s?.processing}
          hint={s && s.processing > 0 ? goiY : undefined}
          locale={locale}
          icon={<SyncOutlined />}
          tone="bg-primary-50 text-primary-700"
          active={trangThai === 'PROCESSING'}
          onClick={() => datTrangThai('PROCESSING')}
        />
        <StatCard
          label={t('chungTu.thChoXacNhan')}
          value={s?.processed}
          locale={locale}
          icon={<ClockCircleOutlined />}
          tone="bg-warning-50 text-warning-700"
          active={trangThai === 'PROCESSED'}
          onClick={() => datTrangThai('PROCESSED')}
        />
        <StatCard
          label={t('chungTu.thDaXacNhan')}
          value={s?.confirmed}
          locale={locale}
          icon={<CheckCircleOutlined />}
          tone="bg-success-50 text-success-700"
          active={trangThai === 'CONFIRMED'}
          onClick={() => datTrangThai('CONFIRMED')}
        />
        <StatCard
          label={t('chungTu.thDaChuyenKho')}
          value={s?.transferred}
          locale={locale}
          icon={<InboxOutlined />}
          tone="bg-subtle text-content-secondary"
          active={trangThai === 'TRANSFERRED'}
          onClick={() => datTrangThai('TRANSFERRED')}
        />
        <StatCard
          label={t('chungTu.thLoiOcr')}
          value={s?.error}
          locale={locale}
          icon={<ExclamationCircleOutlined />}
          tone="bg-danger-50 text-danger-700"
          active={trangThai === 'ERROR'}
          onClick={() => datTrangThai('ERROR')}
        />

        {/* Thanh bộ lọc: các control tự xuống dòng, không bị ẩn. */}
        <div className="col-span-12 flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            className="w-full min-w-0 sm:w-80"
            allowClear
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('chungTu.timTheo')}
            aria-label={t('chungTu.timNhan')}
            value={oTim}
            onChange={(e) => {
              const v = e.target.value;
              setOTim(v);
              clearTimeout(hen.current);
              hen.current = setTimeout(() => {
                setTuKhoa(v);
                setChon([]);
              }, CHO_GO);
            }}
          />
          <Select
            allowClear
            placeholder={t('chungTu.tatCaTrangThai')}
            aria-label={t('chung.trangThai')}
            options={statusOptions}
            value={trangThai}
            onChange={(value) => {
              setTrangThai(value as string | undefined);
              setChon([]);
            }}
            className="w-full sm:w-60"
          />
          <Select
            allowClear
            placeholder={t('chungTu.tatCaLoai')}
            aria-label={t('chungTu.nhanLoaiChungTu')}
            options={typeOptions}
            value={loai}
            onChange={(value) => {
              setLoai(value as DocumentType | undefined);
              setChon([]);
            }}
            className="w-full sm:w-48"
          />
          {coLoc && (
            <Button variant="ghost" size="sm" icon={<CloseOutlined />} onClick={xoaBoLoc}>
              {t('chung.xoaBoLoc')}
            </Button>
          )}
        </div>

        {/* Thanh hành động hàng loạt, chỉ hiện khi có dòng được chọn; nút nêu số lượng. */}
        {coChon && (
          <div
            role="region"
            aria-label={t('chungTu.daChon', { so: chon.length })}
            className="col-span-12 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5"
          >
            <span className="mr-2 text-sm font-medium text-primary-700">{t('chungTu.daChon', { so: chon.length })}</span>
            {coSua && (
              <Button
                variant="outline"
                size="sm"
                icon={<CheckOutlined />}
                loading={thaoTac.dangXacNhan}
                onClick={() => thaoTac.xacNhanNhieu(chon, () => setChon([]))}
              >
                {t('chungTu.xacNhanSo', { so: chon.length })}
              </Button>
            )}
            {coChuyen && (
              <Button variant="outline" size="sm" icon={<SendOutlined />} onClick={() => thaoTac.moChuyenNhieu(chon, () => setChon([]))}>
                {t('chungTu.chuyenSo', { so: chon.length })}
              </Button>
            )}
            {coXoa && (
              <Button variant="outline" size="sm" danger icon={<DeleteOutlined />} onClick={() => thaoTac.moXoaNhieu(chon, () => setChon([]))}>
                {t('chungTu.xoaSo', { so: chon.length })}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="ml-auto" icon={<CloseOutlined />} onClick={() => setChon([])}>
              {t('chungTu.boChon')}
            </Button>
          </div>
        )}

        <div className="col-span-12 overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">{t('chungTu.loiDanhSach', { loi: dichLoi(q.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<DocListItem>
                caption={t('chungTu.bangCaption')}
                rowKey="id"
                dataSource={dong}
                columns={columns}
                loading={dangTai}
                size="middle"
                pagination={false}
                scroll={{ x: 'max-content' }}
                rowSelection={
                  coSua || coXoa || coChuyen
                    ? { selectedRowKeys: chon, onChange: (keys) => setChon(keys as string[]) }
                    : undefined
                }
                locale={{
                  emptyText: dangTai ? (
                    ' '
                  ) : (
                    <Empty
                      icon={FileTextOutlined}
                      title={t(coLoc ? 'chungTu.rongLoc' : 'chungTu.rongThat')}
                      // Rỗng vì bộ lọc thì không có mô tả và không có nút hành động.
                      description={coLoc ? undefined : t('chungTu.rongThatMo')}
                      action={
                        coLoc || !coNhanDang ? undefined : (
                          <Button variant="primary" size="md" icon={<ScanOutlined />} onClick={() => navigate('/xu-ly/nhan-dang')}>
                            {t('chungTu.nhanDangMoi')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {dong.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {q.hasNextPage
                      ? t('chungTu.dangHienConNua', { so: soVN(dong.length, locale) })
                      : t('chungTu.dangHien', { so: soVN(dong.length, locale) })}
                  </span>
                  {q.hasNextPage && (
                    <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                      {t('chung.taiThem')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {thaoTac.hopThoai}
    </div>
  );
}
